import { getCollisionStateConfig, isKnownCollisionState } from '@/components/collision/collisionStateConfig';
import { Tooltip } from 'primereact/tooltip';
import { type FC, useId } from 'react';
import { useTranslation } from 'react-i18next';

interface CollisionStatusTagProps {
  /** Collision simulation state from the backend; renders nothing when null/absent. */
  state: string | null | undefined;
}

/**
 * Compact collision-status badge for project list views (card + table row).
 *
 * Reuses the shared collision state config so the label, icon, and colors
 * match the project-detail collision panel. Renders nothing when there is
 * no collision data yet.
 */
export const CollisionStatusTag: FC<CollisionStatusTagProps> = ({ state }) => {
  const { t } = useTranslation();
  // CSS-class-safe id so each tag instance gets its own PrimeReact tooltip target.
  const hintClass = `collision-status-hint-${useId().replace(/:/g, '')}`;

  if (!state) {
    return null;
  }

  const cfg = getCollisionStateConfig(state);
  const label = isKnownCollisionState(state) ? t(`projectDetail.collisionStates.${state}`, state) : state;
  const Icon = cfg.icon;
  const tooltip = t('projectDetail.collisionDetectionState', 'Collision detection state');

  return (
    <>
      <Tooltip target={`.${hintClass}`} position="top" />
      <span
        className={`${hintClass} inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded cursor-help ${cfg.tagBg} ${cfg.tagText}`}
        data-pr-tooltip={tooltip}
      >
        <Icon size={12} className={state === 'running' ? 'animate-spin' : undefined} />
        <span className="truncate max-w-[120px]">{label}</span>
      </span>
    </>
  );
};
